import { useEffect, useMemo, VFC } from 'react';
import { useParams } from 'react-router-dom';
import { useMeterHardwareItems } from 'hooks/useMeterHardwares';
import { Layout } from 'components/Layout/Layout';
import { HardwareCategory, SerializedInventoryMeterOutput } from '@enmon/commons/lib/tools';
import { StepsProgressBar } from 'components/progress/ProgressBar';
import { useIntl } from 'react-intl';
import { pick } from 'rambda';
import { useContextMeter } from 'hooks/useContextMeter';
import { MeterContext } from 'contexts/Meter';
import { isConsumptionRegister } from '@enmon/commons';
import { getContentByStep } from './getContentByStep';
import { getFooterByStep } from './getFooterByStep';
import { useInstallMeterStepsStore } from './components/store';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useStore } from './useStore';
import { INSTALL_METER_STEPS_COUNT } from './const/common';
import { InstallMeterStepTranslated as messages } from './messages';

const categories = Object.values(HardwareCategory).filter(category => category !== HardwareCategory.Other);

export const InstallMeterPage: VFC = () => {
  const intl = useIntl();
  const { setInstallationDetailStepValues, setHardwares, setEnmonMeter, setInventoryMeter } = useStore();

  const { currentStep } = useInstallMeterStepsStore();

  const { id: inventoryMeterId } = useParams();

  const toolsId = parseInt(inventoryMeterId ?? '');
  const { enmonMeter, inventoryMeter, isLoading: isLoadingMeter } = useContextMeter(toolsId);

  const contextValue = useMemo(
    () => ({ enmonMeter, inventoryMeter, isLoading: isLoadingMeter }),
    [enmonMeter, inventoryMeter, isLoadingMeter],
  );

  const {
    data: meterHardwareItems,
    loading: isLoadingMeterHardwares,
    error: hardwaresError,
  } = useMeterHardwareItems({
    inventoryMeter: toolsId,
    categories,
  });

  const isLoading = isLoadingMeter || isLoadingMeterHardwares;
  const error = hardwaresError;

  useEffect(() => {
    if (!inventoryMeter || !enmonMeter) {
      return;
    }

    setEnmonMeter(enmonMeter);
    setInventoryMeter(inventoryMeter);

    setInstallationDetailStepValues(() => ({
      meterMountTechnology: inventoryMeter.meter_mount_technology,
      outputs:
        inventoryMeter?.inventory_meter_outputs?.map(output =>
          pick(
            ['id', 'technology', 'is_occupied', 'remote_reading_owner'] satisfies Array<
              keyof SerializedInventoryMeterOutput
            >,
            output,
          ),
        ) ?? [],
      registers: enmonMeter.registers.filter(isConsumptionRegister).map(register => ({
        counter: null,
        counterPhotos: [],
        ...register,
      })),
    }));
  }, [enmonMeter, setInstallationDetailStepValues, inventoryMeter, setEnmonMeter, setInventoryMeter]);

  useEffect(() => {
    setHardwares(() =>
      (meterHardwareItems?.hardwareItems.data ?? [])
        .filter(meterHardware => meterHardware.deactivated_at === null)
        .map(meterHardware => ({
          id: parseInt(meterHardware.id, 10),
          serial_number: meterHardware.serial_number,
          hardwareName: meterHardware.hardware?.name ?? '',
        })),
    );
  }, [meterHardwareItems?.hardwareItems.data, setHardwares]);

  const PageContent = getContentByStep(currentStep);
  const PageFooter = getFooterByStep(currentStep);

  const renderContent = () => {
    if (isLoading) {
      return <LoadingSpinner />;
    }

    if (error) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Failed to fetch required data</p>
          {/* You could add more specific error messages here based on the error type */}
        </div>
      );
    }

    return <PageContent key={toolsId} />;
  };

  return (
    <MeterContext.Provider value={contextValue}>
      <Layout
        footerContent={<PageFooter />}
        headerContent={intl.formatMessage(messages[currentStep])}
        progressBarContent={<StepsProgressBar progressStep={currentStep} steps={INSTALL_METER_STEPS_COUNT} />}
      >
        {renderContent()}
      </Layout>
    </MeterContext.Provider>
  );
};
